import React, { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { AcaSelect } from "./components/forms/acaSelect";
import { DateGrid } from "./components/forms/dateGrid";
import { Option } from '@mui/base/Option';
import { displayErrorMessage, displayLoader, ConnectionContext, getConnectionInfo } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { MatchGrid } from "./components/matchGrid";
import { StandingsAndBracket } from "./components/tables/standingsAndBracket";


const SchedulesApp = (props) => {
    const { 
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const [connectionInfo, setConnectionInfo] = useState(null);

    const [ seasons, setSeasons ] = useState(null);
    const [ selectedSeason, setSelectedSeason ] = useState(null);

    const [ competitions, setCompetitions ] = useState(null);
    const [ selectedCompetition, setSelectedCompetition ] = useState(null);
    const [ selectedCompetitionData, setSelectedCompetitionData ] = useState(null);

    const [ scheduleDates, setScheduleDates ] = useState(null);
    const [ selectedScheduleDate, setSelectedScheduleDate ] = useState(null);

    const [ standings, setStandings ] = useState(null);

    /**
     * Get our connection info, filters for the schedules, and the first round of data for matches and standings
     */
    useEffect(() => {
        setIsLoaded(false);

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        let args = [
            {
                key: 'competitions',
                type: 'has_seasons'
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set Competitions
                if ( result.data.competitions ) {
                    setCompetitions(result.data.competitions);
                    setSelectedCompetition(result.data.competitions[0].id);
                    setSelectedCompetitionData(result.data.competitions[0]);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);

    /**
     * When competition is updated, get the new list of seasons
     */
    useEffect(() => {
        if ( !selectedCompetitionData ) return;
        if ( !selectedCompetitionData.hasOwnProperty('seasons') || !selectedCompetitionData.seasons ) {
            console.log('This competition has no seasons');
            return;
        }

        let seasonsArray = Object.keys(selectedCompetitionData.seasons).map((key) => selectedCompetitionData.seasons[key]);
        if ( selectedCompetitionData.hasOwnProperty('seasons_order') ) {
            let sortedSeasonsArray = [];
            let sortingKey = selectedCompetitionData.seasons_order;
            seasonsArray.forEach((season) => {
                const index = sortingKey.indexOf(season.name);
                sortedSeasonsArray[index] = season;
            });
            seasonsArray = sortedSeasonsArray;
        }
        setSeasons(seasonsArray);
        setSelectedSeason(seasonsArray[0]);

    }, [selectedCompetitionData]);

    /**
     * When season changes, get the new list of schedule dates
     */
    useEffect(() => {
        if ( !selectedSeason ) return;
        // If we're already loading, don't run this
        if ( !isLoaded ) return;
        setIsLoaded(false);

        let args = [
            {
                key: 'scheduleDates',
                type: 'specific',
                competition: selectedCompetition,
                season: selectedSeason.name,
            },
            {
                key: 'standings',
                type: 'specific',
                competition: selectedCompetition,
                season: selectedSeason.name,
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set schedule
                if ( result.data.scheduleDates ) {
                    setScheduleDates(result.data.scheduleDates);
                } else {
                    setScheduleDates(null);
                    setSelectedScheduleDate(null);
                }

                // Set standings
                if ( result.data.standings ) {
                    setStandings(result.data.standings);
                } else {
                    setStandings(null);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, [selectedSeason]);


    // Change the selected season
    const handleSeasonChange = (event, value) => {
        setSelectedScheduleDate(null);
        // Use the selected season's name to get the proper season
        const newSeason = seasons.find((season) => season.name == value);
        setSelectedSeason(newSeason);
    }

    // Change the selected competition
    const handleCompetitionChange = (event, value) => {
        // Set date and season to null for now, we'll pull the dynamic data in a moment
        // Solves for multiple database queries potentially firing in the wrong order
        setSelectedScheduleDate(null);
        setSelectedSeason(null);

        // Set the competition info
        setSelectedCompetition(value);
        setSelectedCompetitionData(competitions.find((el) => el.id == value));
    }

    // Change the selected date
    const handleDateChange = (date = null) => {
        if ( !date ) return;
        setSelectedScheduleDate(date);
    }


    // Bail if we don't have data yet
    if (!isLoaded ) {
        return displayLoader('acaLoader--main');
    }

    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <section className="acaPage acaPage--schedules">
                { /** Schedules */ }
                <div className="acaSchedules">

                    <div className="acaBar acaBar--schedules">
                        <div className="acaWrapper acaWrapper--withPadding acaBar__inner">
                            <div className="acaBar__col">
                                <h2 className="acaH3">Select a league and season to see their schedule</h2>
                            </div>
                            <div className="acaBar__col acaBar__col--dropdowns">

                                { /** Competitions Select */ }
                                { competitions ? (
                                    <AcaSelect onChange={handleCompetitionChange} extraClasses="acaSelect--wide" defaultValue={selectedCompetition ? selectedCompetition : ''} id="schedule-competition" name="schedule-competition">
                                        { competitions.map((competition, i) => (
                                            <Option key={i} value={competition.id}>{competition.name}</Option>
                                        ))}
                                    </AcaSelect>
                                ) : '' }

                                { /** Seasons Select */ }
                                { seasons ? (
                                    <AcaSelect onChange={handleSeasonChange} defaultValue={selectedSeason ? selectedSeason.name : ''} id="schedule-season" name="schedule-season">
                                        { seasons.map((season, i) => (
                                            <Option key={i} value={season.name}>{season.name}</Option>
                                        ))}
                                    </AcaSelect>
                                ) : '' }

                            </div>
                        </div>
                    </div>

                    <DateGrid dates={scheduleDates} onChange={handleDateChange}></DateGrid>

                    { selectedScheduleDate != null && selectedSeason ? (
                        <div className="acaWrapper acaWrapper--withPadding acaScheduleGrid__wrapper">
                            <h3 className="acaH2 acaScheduleGrid__title">{ selectedScheduleDate.toLocaleString("en-US", { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }</h3>
                            <MatchGrid season={selectedSeason.name} competition={selectedCompetition} date={selectedScheduleDate} />
                        </div>
                    ) : (
                        <>
                            { displayErrorMessage('There are no dates available for this competition season. Please check back later.', 'acaError--light') }
                        </>
                    ) }

                    


                </div>

                { /** Standings */ }
                <StandingsAndBracket season={selectedSeason} showTitle={true} standings={standings} />
                

            </section>
        </ConnectionContext.Provider>
    );
}

export default SchedulesApp;

// Render Match Page
const appContainer = document.getElementById('schedulesApp');
const root = createRoot(appContainer);
root.render(<SchedulesApp />);